/**
 * Authentication state manager
 * Maintains in-memory authentication state and provides initialization logic
 */

import type { AuthState } from './types';
import { getKey as getStoredKey, clearKey, loadCode } from './storage';
import { validateKey } from './api';
import { redeemCode } from './redeem';

/**
 * In-memory authentication state
 */
let authState: AuthState = {
  authenticated: false,
  key: null,
};

export type InitAuthOptions = {
  /**
   * When true, validate the stored license in Vite dev the same way as production
   * (default: false — dev uses a `DEV-MODE` bypass because the license server often blocks localhost).
   */
  forceLicenseGateInDev?: boolean;
};

/**
 * Initialize authentication on app startup
 * Reads license key from localStorage and validates it
 * If invalid, clears localStorage and sets state to unauthenticated
 */
export async function initAuth(options?: InitAuthOptions): Promise<void> {
  console.log('[Auth Manager] Initializing authentication');
  const forceLicenseGateInDev = options?.forceLicenseGateInDev === true;

  // Skip registration wall in dev — the license server CORS-blocks localhost
  if (import.meta.env.DEV && !forceLicenseGateInDev) {
    console.log('[Auth Manager] Dev mode detected, skipping registration');
    updateAuthState(true, 'DEV-MODE');
    return;
  }

  if (import.meta.env.DEV && forceLicenseGateInDev) {
    console.log('[Auth Manager] Dev mode with forceLicenseGateInDev: validating license like production');
  }

  // Preferred path (PIPH-291): a stored redemption code is the long-lived
  // identity. Re-call /redeemCode to mint a fresh JWT in-memory. The
  // server's idempotent re-mint (PIPH-290) returns success for the
  // original redeemer without rewriting the code or license docs.
  //
  // Failure here is non-fatal: we fall through to the legacy JWT path so
  // pre-rework users (JWT-only in storage) still authenticate. Transient
  // failures (network, Firebase not ready) also fall through, so a stuck
  // boot doesn't strand the user.
  const storedCode = loadCode();
  if (storedCode) {
    console.log('[Auth Manager] Found stored redemption code, re-minting JWT…');
    try {
      await redeemCode(storedCode);
      // redeemCode() already called updateAuthState on success.
      return;
    } catch (err) {
      console.log(
        '[Auth Manager] Code re-mint failed; falling back to legacy JWT validation:',
        err instanceof Error ? err.message : err,
      );
      // Intentionally NOT clearing the code here — failure may be
      // transient (network, Firebase not ready). On permanent rejection
      // (revoked/expired) the server returns a user-facing error from a
      // later user-initiated redeem; the boot path stays graceful.
    }
  }

  // Legacy path: pre-rework users have a JWT in storage but no
  // redemption code. Validate the JWT and accept if valid.
  const storedKey = getStoredKey();

  if (!storedKey) {
    console.log('[Auth Manager] No stored license key found');
    updateAuthState(false, null);
    return;
  }

  console.log('[Auth Manager] Found stored license key, validating…');

  try {
    const isValid = await validateKey(storedKey);

    if (isValid) {
      console.log('[Auth Manager] License key is valid');
      updateAuthState(true, storedKey);
    } else {
      console.log('[Auth Manager] License key is invalid, clearing storage');
      clearKey();
      updateAuthState(false, null);
    }
  } catch (error) {
    console.error('[Auth Manager] Error during initialization:', error);
    clearKey();
    updateAuthState(false, null);
  }
}

/**
 * Check if the user is currently authenticated
 */
export function isAuthenticated(): boolean {
  return authState.authenticated;
}

/**
 * Get the current license key
 */
export function getKey(): string | null {
  return authState.key;
}

/**
 * Update the in-memory authentication state
 * Internal function used by auth modules
 */
export function updateAuthState(authenticated: boolean, key: string | null): void {
  authState = { authenticated, key };
  console.log('[Auth Manager] Auth state updated:', { authenticated, hasKey: !!key });
}
